import React from 'react';

import { Tooltip } from '@/components/Tooltip';
import { cn } from '@/utils/classname';

/**
 * StatusChip - Standardized status indicator component with solid pill styling
 *
 * Replaces StatusBadge (dashed border) with a solid background pill design
 * that matches RiskTag styling for visual consistency across all tables.
 *
 * Visual treatment:
 * - Rounded pill with solid background and subtle border
 * - Icon + label layout with gap spacing
 * - 10px semibold uppercase text
 * - Color-coded via variant prop
 * - Text truncation for long labels
 *
 * @param label - Status label text (e.g., "Active", "Pending", "Opened")
 * @param variant - Color variant for the status chip
 * @param icon - Optional icon element to display (usually Heroicons 24px outline)
 * @param tooltip - Optional tooltip text shown on hover
 * @param className - Additional Tailwind classes for customization
 *
 * @example
 * ```tsx
 * // With icon and tooltip
 * <StatusChip
 *   label="Active"
 *   variant="info"
 *   icon={<CheckCircleIcon className="size-3 shrink-0" />}
 *   tooltip="Last seen less than 2 days ago"
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Without icon
 * <StatusChip
 *   label="Failed"
 *   variant="critical"
 * />
 * ```
 */

export type StatusChipVariant =
  | 'info' // Blue: Active, Approved, Remediated, Completed
  | 'high' // Orange: Triaged, Pending, Inactive
  | 'critical' // Red: Opened, Expiring, Rejected, Failed
  | 'neutral' // Green: Frozen, Accepted
  | 'muted' // Gray: Deleted, Unknown, Queued
  | 'default';

export interface StatusChipProps {
  label: string;
  variant?: StatusChipVariant;
  icon?: React.ReactNode;
  tooltip?: string;
  className?: string;
}

const variantStyles: Record<StatusChipVariant, string> = {
  info: 'bg-chariot-stroke-info border-chariot-stroke-info text-white',
  high: 'bg-chariot-stroke-high border-chariot-stroke-high text-white',
  critical:
    'bg-chariot-stroke-critical border-chariot-stroke-critical text-white',
  neutral:
    'bg-chariot-stroke-low border-chariot-stroke-low text-white dark:bg-chariot-stroke dark:border-chariot-stroke',
  muted:
    'bg-chariot-background-secondary border-chariot-stroke text-chariot-text-primary',
  default:
    'bg-chariot-background-secondary border-chariot-stroke text-chariot-text-primary',
};

export const StatusChip: React.FC<StatusChipProps> = ({
  label,
  variant = 'default',
  icon,
  tooltip,
  className,
}) => {
  const chip = (
    <div
      className={cn(
        // Base styles matching RiskTag
        'inline-flex w-fit items-center justify-center rounded-full border',
        'px-2 py-1 text-[10px] font-semibold uppercase',
        // Icon gap
        icon && 'gap-1.5',
        // Variant colors
        variantStyles[variant],
        className
      )}
    >
      {icon && <span className="size-3 shrink-0">{icon}</span>}
      <span className="truncate">{label}</span>
    </div>
  );

  return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip;
};
